/**
 * iOS-safe attachment transport helpers.
 * ---------------------------------------
 *
 * Why this exists: on iOS Safari the `download` attribute on `<a>` tags is
 * silently ignored when the href points to a different path on the same
 * origin — the browser navigates the current tab to the file URL instead of
 * triggering a download. Combined with `Content-Disposition: attachment`
 * responses, this kicks the user out of the SPA into a Safari "Loading
 * interrupted" page (German: "Das Laden dieser Webseite wurde unterbrochen").
 *
 * The reliable workaround is:
 *
 *   1. Fetch the file via XHR/`fetch` with credentials: 'include' so cookies
 *      (and optional auth headers) are attached.
 *   2. Convert the response to a Blob.
 *   3. Create an in-memory `blob:` URL via `URL.createObjectURL`.
 *   4. For DOWNLOAD: programmatically click a hidden `<a download>` whose
 *      href is the blob URL. iOS Safari DOES honor `download` for blob URLs
 *      (the only carve-out in the otherwise-restricted policy).
 *   5. For OPEN-IN-NEW-TAB: pre-open a blank tab synchronously inside the
 *      user gesture (so popup blockers don't intervene), then once the blob
 *      URL is ready, set the new tab's location to it. iOS QuickLook then
 *      renders PDFs/images natively in that tab while leaving the SPA tab
 *      untouched.
 *
 * Both helpers swallow auth errors into a friendly toast — they NEVER
 * navigate the current tab to the raw API URL on failure. Callers stay
 * inside the inbox/email-detail view.
 */

import { toast } from "sonner";

/**
 * iOS detection (iPhone, iPod, iPad — including iPadOS 13+ which reports
 * as MacIntel with touch points). Used to decide whether to route a
 * download through the system Share Sheet instead of the blob anchor
 * trick (the latter triggers an "App öffnen?" dialog on iOS Firefox/
 * Klar/etc. with no working save action).
 */
export function isIOS() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  if (/iPad|iPhone|iPod/.test(ua)) return true;
  if (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1) return true;
  return false;
}

/**
 * Force-download a remote file via blob + object URL.
 *
 * @param {string} url               — fully qualified URL (or same-origin
 *                                     path) to fetch.
 * @param {string} filename          — suggested filename for the download.
 * @param {{headers?: Record<string,string>, errorMessage?: string}} [opts]
 *
 * @returns {Promise<boolean>} true on success, false on any failure.
 */
export async function downloadFileSafe(url, filename, opts = {}) {
  const { headers, errorMessage = "Download failed. Try again." } = opts;
  let objectUrl = null;
  try {
    const response = await fetch(url, {
      credentials: "include",
      headers: headers || undefined,
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const blob = await response.blob();
    objectUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = objectUrl;
    a.download = filename || "download";
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    return true;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn("[attachments] downloadFileSafe failed:", err);
    toast.error(errorMessage);
    return false;
  } finally {
    // Give the browser a moment to read the blob before revoking. Safari
    // sometimes initiates the download asynchronously, so a brief delay
    // before revocation prevents the download being cut short.
    if (objectUrl) {
      setTimeout(() => {
        try { URL.revokeObjectURL(objectUrl); } catch { /* noop */ }
      }, 1500);
    }
  }
}


/**
 * Open a remote file in a new browser tab, via blob + object URL so the
 * raw `/api/...` URL never appears in any tab's history.
 *
 * Critical: the placeholder window MUST be opened synchronously inside the
 * user gesture. We open `about:blank` first, then redirect to the blob URL
 * once the fetch completes. If we waited until after the fetch, iOS would
 * popup-block.
 *
 * @param {string} url               — fully qualified URL (or same-origin
 *                                     path) to fetch.
 * @param {{headers?: Record<string,string>, errorMessage?: string}} [opts]
 *
 * @returns {Promise<boolean>} true on success, false on any failure.
 */
export async function openFileSafe(url, opts = {}) {
  const {
    headers,
    errorMessage = "Could not open file. Please use Download instead.",
  } = opts;

  // 1. Pre-open the window synchronously. This MUST happen before any
  //    `await` so we're still inside the user gesture and the popup
  //    blocker doesn't engage.
  const tab = window.open("about:blank", "_blank");

  try {
    const response = await fetch(url, {
      credentials: "include",
      headers: headers || undefined,
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    if (!tab) {
      // Popup blocked even though we opened synchronously. Fall back to
      // a same-tab blob navigation? Avoid — that can still confuse iOS.
      // Surface a friendly toast instead so the user uses the Download
      // button.
      throw new Error("popup_blocked");
    }
    tab.location.href = objectUrl;
    // We deliberately do NOT call URL.revokeObjectURL here: the new tab is
    // still using the URL. The browser cleans these up automatically when
    // the originating document is unloaded.
    return true;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn("[attachments] openFileSafe failed:", err);
    if (tab) {
      try { tab.close(); } catch { /* noop */ }
    }
    toast.error(errorMessage);
    return false;
  }
}



/**
 * iOS-aware "save this file" action.
 *
 * Why this exists: on iOS Safari/Firefox/Klar, the blob-anchor trick used
 * by `downloadFileSafe` reliably ends in an "App öffnen?" dialog whose
 * "Öffnen" button does nothing — there's no native action wired up. iOS
 * does NOT have the desktop notion of "save to a chosen folder"; the
 * correct equivalent is the system Share Sheet (Save to Files / AirDrop /
 * Mail / "Open in…").
 *
 * Strategy:
 *   1. On NON-iOS or when `navigator.canShare` is unavailable, defer to
 *      `downloadFileSafe` — desktop behavior is unchanged.
 *   2. On iOS with Web Share Level 2 (`canShare({files:[...]})`), fetch
 *      the URL, wrap the response into a `File` with the correct MIME,
 *      and call `navigator.share({files, title})`. iOS presents the
 *      native Share Sheet; the user picks "Save to Files" or any other
 *      destination. Cancellation throws AbortError — swallowed silently.
 *   3. If `canShare` rejects the file payload (some MIME types are
 *      blocked) we fall back to the same blob anchor click as
 *      `downloadFileSafe`. iOS will at least preview the file inline.
 *
 * The function NEVER navigates the SPA tab away on failure — failures
 * surface as a sonner toast.
 *
 * @param {string} url
 * @param {string} filename
 * @param {{headers?: Record<string,string>, mimeType?: string, errorMessage?: string}} [opts]
 * @returns {Promise<boolean>}
 */
export async function shareOrDownloadFile(url, filename, opts = {}) {
  const {
    headers,
    errorMessage = "Download failed. Try again.",
  } = opts;

  // P0 fix (Apr 30 2026): `navigator.share({files})` on iOS WKWebView
  // wrappers (Klar, Firefox-iOS, in-app browsers, Emergent preview
  // frame) has been observed to blank the SPA after the Share Sheet
  // closes — the underlying `<iframe>` host swaps out of the render
  // tree when iOS reactivates the tab. The symptom is a white screen
  // with only the Emergent badge visible and the user losing their
  // temporary inbox session.
  //
  // We unconditionally route every download through the blob-anchor
  // path. iOS Safari 15+ honors `<a download>` for `blob:` URLs by
  // triggering the native "Save to Files / Share" dialog without
  // replacing the SPA tab. Cancellation leaves the modal open and
  // the inbox intact.
  return downloadFileSafe(url, filename, { headers, errorMessage });
}
